import { cn } from "tailwind-variants";
import type { ContentFeedItemDto } from "#/_generated/umbraco/delivery-api/models";
import ContentFeedItem from "#/_packages/umbrella/features/content-feed/components/content-feed-item.tsx";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	AnimationEffectType,
	AnimationScrollType,
	ContentFeedSortBy,
	HeadingLevel,
	Intent,
	Orientation,
	ReadWidthLength,
	Variant,
} from "#/_packages/umbrella/types";
import { toDate } from "#/_packages/umbrella/utils/date-utils.ts";

export type ContentFeedOrientation =
	| Orientation
	| "orientation-horizontal"
	| "orientation-vertical";

export type ContentFeedProps = {
	id?: string;
	items: ContentFeedItemDto[];
	sortBy?: ContentFeedSortBy;
	intentColor?: Intent;
	typeVariant?: Variant;
	orientation?: ContentFeedOrientation;
	imagePositionEnd?: boolean;
	hideHeading?: boolean;
	hideIntro?: boolean;
	hidePublishDate?: boolean;
	hideImage?: boolean;
	className?: string;
	headingLevel?: HeadingLevel;
	readWidthToggle?: boolean;
	readWidthLength?: ReadWidthLength;
	animationScrollType?: AnimationScrollType;
	animationEffectType?: AnimationEffectType | string;
	animationHoverType?: string[];
	applyAnimationToChildren?: boolean;
	staggerAnimation?: boolean;
};

function sortItems(
	items: readonly ContentFeedItemDto[],
	sortBy: ContentFeedSortBy,
) {
	const getPublishDate = (item: ContentFeedItemDto) =>
		item.publishDate ? (toDate(item.publishDate)?.getTime() ?? 0) : 0;
	const copy = [...items];

	switch (sortBy) {
		case "publishDateAscending":
			return copy.sort((a, b) => getPublishDate(a) - getPublishDate(b));
		case "publishDateDescending":
			return copy.sort((a, b) => getPublishDate(b) - getPublishDate(a));
		case "sortOrderAscending":
			return copy.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
		case "sortOrderDescending":
			return copy.sort((a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0));
		default:
			return copy;
	}
}

function toReadWidthLengthClass(length: ReadWidthLength) {
	switch (length) {
		case "narrow":
			return "read-width-narrow";
		case "wide":
			return "read-width-wide";
		default:
			return "read-width-normal";
	}
}

export function ContentFeed({
	id,
	items,
	sortBy = "publishDateDescending",
	intentColor = "none",
	typeVariant = "solid",
	orientation = "orientation-vertical",
	imagePositionEnd = false,
	hideHeading = false,
	hideIntro = false,
	hidePublishDate = false,
	hideImage = false,
	className,
	headingLevel = 3,
	readWidthToggle = false,
	readWidthLength = "normal",
	animationScrollType = "none",
	animationEffectType = "none",
	animationHoverType = [],
	applyAnimationToChildren = false,
	staggerAnimation = false,
}: ContentFeedProps) {
	const sortedItems = sortItems(items, sortBy);
	const rootAnimationType = applyAnimationToChildren ? "" : animationScrollType;
	const childAnimationType = applyAnimationToChildren
		? animationScrollType
		: "";
	const childAnimationEffectType = applyAnimationToChildren
		? animationEffectType
		: "none";
	const staggerClass =
		applyAnimationToChildren && staggerAnimation ? "anim-stagger" : null;
	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);

	return (
		<div
			id={id}
			className={cn(
				"block__content-feed w-full",
				readWidthToggle && "read-width",
				readWidthToggle && toReadWidthLengthClass(readWidthLength),
				rootAnimationType !== "none" && rootAnimationType,
				className,
			)}
		>
			{sortedItems.length > 0 ? (
				<div
					className={cn(
						"content-feed content-feed-variant-teasers",
						staggerClass,
					)}
				>
					{sortedItems.map((item) => {
						return (
							<ContentFeedItem
								imagePositionEnd={imagePositionEnd}
								intent={intentColor}
								variant={typeVariant}
								hideIntro={hideIntro}
								hideImage={hideImage}
								hidePublishDate={hidePublishDate}
								hideHeading={hideHeading}
								key={item.id}
								item={item}
								orientation={orientation}
								headingLevel={headingLevel}
								animationScrollType={childAnimationType}
								animationEffectType={childAnimationEffectType}
								hoverItemClasses={hoverItemClasses}
								hoverMediaClasses={hoverMediaClasses}
							/>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
